import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useAsyncInterval } from '../../utils/hooks';
import { postDocAssistantCapture, getDocAssistantCaptureStatus, postDocAssistantAssign } from '../../../api/emailsApi/docAssistant';
import { setDocAssistantCaptured, setIsSearchingRelatedRecords } from '../../actions/mail';
import { applyDocAssistantFileResults, clearDocAssistantProcessing } from '../../actions/files';
import { refreshSavedLocations } from '../../utils/savedLocations';
import { logError } from '../../utils/request';

const POLL_INTERVAL_MS = 10000;
// Poll on a slight backoff (up to the cap) so a long-running pipeline is not hammered.
const POLL_MAX_INTERVAL_MS = 30000;
const POLL_BACKOFF_STEP_MS = 5000;
// Classification + matter-matching can run for several minutes; keep waiting up to 15 minutes rather
// than concluding "no matter" early.
const MAX_POLL_DURATION_MS = 15 * 60 * 1000;
// The rename step runs AFTER a file is already terminal, so keep polling a few ticks past 'complete'
// to pick up the new names before stopping.
const RENAME_GRACE_TICKS = 5;
const NO_MATTER_MESSAGE = 'No matter assigned. Manually select and add at least one saved location.';
const CAPTURE_FAILED_MESSAGE = 'Assign with ACE failed. Try again or manually add a saved location.';

// Owns the Doc Assistant capture poll loop and view transitions. Mounted once in SaveToContainer so the
// poll survives the search <-> selected-record navigation a match triggers; the button and search
// skeleton read the returned state through DocAssistantCaptureContext.
export const useDocAssistantCapture = () => {
  const dispatch = useDispatch();

  const enabled = useSelector((state) => state.mail.settings?.docAssistant?.enabled);
  const emailId = useSelector((state) => state.mail.activeEmail);
  const ownerEmail = useSelector((state) => state.mail.ownerEmail);
  const isComposingEmail = useSelector((state) => state.mail.isComposingEmail);
  const docrioNamespace = useSelector((state) => state.mail.settings?.override?.docrioNamespace || 'litify_docs');

  // idle | searching | matched | noMatter — 'matched' keeps polling for files still in the pipeline
  // after the first match already navigated the pane to the selected-record (documents) view.
  const [status, setStatus] = useState('idle');
  const [banner, setBanner] = useState(null);
  // The poll delay grows over the wait (backoff); pollDeadline is the wall-clock give-up time.
  const [pollDelay, setPollDelay] = useState(POLL_INTERVAL_MS);
  const pollDeadline = useRef(0);
  // Extra poll ticks allowed after 'complete' so the rename step's new names can land.
  const postCompleteTicks = useRef(0);
  // Set once the first match navigates the pane to the documents view.
  const matchedRef = useRef(false);
  // Guards the async finalize (assign or no-matter) so a later poll tick cannot re-run it.
  const finalizingRef = useRef(false);
  // useAsyncInterval keeps firing while a slow status request is pending; overlapping ticks are skipped.
  const pollInFlightRef = useRef(false);

  const dismissBanner = () => setBanner(null);

  const finishWithoutMatter = () => {
    // Capture confirmed but no auto-assigned matter: files are parked, the user picks the record.
    dispatch(setDocAssistantCaptured(true));
    setBanner(NO_MATTER_MESSAGE);
    setStatus('noMatter');
  };

  const failCapture = () => {
    // Nothing was parked, so the captured flag must stay off or Add would route through assign.
    setBanner(CAPTURE_FAILED_MESSAGE);
    setStatus('idle');
  };

  const stopPolling = () => {
    dispatch(clearDocAssistantProcessing());
    setStatus('idle');
  };

  // Matched files are already reparented, so getEmail reflects them before assign runs. Leave the search
  // view for the documents view; the remaining files keep rendering as skeletons there until the poll
  // finishes.
  const showMatched = async () => {
    try {
      await refreshSavedLocations(dispatch, { emailId, ownerEmail, docrioNamespace });
    } catch (e) {
      logError(e);
    }
    dispatch(setIsSearchingRelatedRecords(false));
    setStatus('matched');
  };

  // Assign waits for every file so each attachment's independently matched matter is included.
  const finishWithMatters = async (relatedTos) => {
    try {
      // Doc Assistant already reparented the files; assign only writes the timeline/thread bookkeeping
      // (filesAlreadyAssigned skips the reparent) so the matched matters show as saved locations.
      await postDocAssistantAssign({ externalId: emailId, relatedTos, filesAlreadyAssigned: true });
      // Pull the final reflection so every matched file (and its reparented name) is saved.
      await refreshSavedLocations(dispatch, { emailId, ownerEmail, docrioNamespace });
      dispatch(setDocAssistantCaptured(false));
      if (!matchedRef.current) {
        matchedRef.current = true;
        dispatch(setIsSearchingRelatedRecords(false));
      }
      stopPolling();
    } catch (e) {
      logError(e);
      dispatch(clearDocAssistantProcessing());
      finishWithoutMatter();
    }
  };

  // An automated capture may already be running when the pane opens; pick up its progress so the
  // searching view shows without the button ever being pressed.
  useEffect(() => {
    if (!enabled || isComposingEmail || !emailId) {
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const response = await getDocAssistantCaptureStatus(emailId);
        if (!cancelled && response?.data?.status === 'processing') {
          pollDeadline.current = Date.now() + MAX_POLL_DURATION_MS;
          setPollDelay(POLL_INTERVAL_MS);
          postCompleteTicks.current = 0;
          matchedRef.current = false;
          finalizingRef.current = false;
          setStatus('searching');
        }
      } catch (e) {
        logError(e);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [emailId, enabled, isComposingEmail]);

  useAsyncInterval(async () => {
    if (finalizingRef.current || pollInFlightRef.current) {
      return;
    }
    pollInFlightRef.current = true;
    // Ease off the pipeline as the wait grows.
    setPollDelay((prev) => Math.min(prev + POLL_BACKOFF_STEP_MS, POLL_MAX_INTERVAL_MS));
    try {
      const response = await getDocAssistantCaptureStatus(emailId);
      if (finalizingRef.current) {
        return;
      }
      const { status: captureStatus, matches, relatedToId, relatedToApiName, files: fileResults } = response?.data || {};
      // Attachments are matched independently, so a capture can resolve to several matters.
      // A backend not yet deployed with multi-matter support omits `matches` entirely and only
      // sends the legacy scalar pair; fall back to a one-element list so a staggered rollout
      // (new add-in, old backend) doesn't misread a real match as "no match".
      const relatedTos = matches || (relatedToId && relatedToApiName ? [{ relatedToId, relatedToApiName }] : []);
      // Per-file progress streams in every tick: finished files pick up their pipeline-assigned
      // names, unfinished ones render as skeletons in the Documents section.
      if (fileResults?.length) {
        dispatch(applyDocAssistantFileResults(fileResults));
      }
      const deadlinePassed = Date.now() >= pollDeadline.current;
      if (captureStatus === 'complete' && !relatedTos.length) {
        finalizingRef.current = true;
        finishWithoutMatter();
        return;
      }
      if (captureStatus === 'complete') {
        // 'complete' means every file reached a terminal pipeline status. Rename runs after a file is
        // already terminal, so wait for the new names (or a short grace budget) before assigning,
        // otherwise the rows keep their original names.
        const namesSettled = (fileResults || []).every((file) => !file.complete || file.fileName);
        if (namesSettled || postCompleteTicks.current >= RENAME_GRACE_TICKS || deadlinePassed) {
          finalizingRef.current = true;
          await finishWithMatters(relatedTos);
          return;
        }
        postCompleteTicks.current += 1;
      } else if (deadlinePassed) {
        // Budget exhausted: assign whatever already matched rather than dropping it; surface failure
        // only when nothing did.
        finalizingRef.current = true;
        if (relatedTos.length) {
          await finishWithMatters(relatedTos);
        } else {
          dispatch(clearDocAssistantProcessing());
          failCapture();
        }
        return;
      }
      if (relatedTos.length && !matchedRef.current) {
        // The first finished-and-matched file navigates the pane to the documents view immediately.
        matchedRef.current = true;
        await showMatched();
      }
    } catch (e) {
      logError(e);
      if (Date.now() >= pollDeadline.current) {
        finalizingRef.current = true;
        if (matchedRef.current) {
          stopPolling();
        } else {
          dispatch(clearDocAssistantProcessing());
          failCapture();
        }
      }
    } finally {
      pollInFlightRef.current = false;
    }
  }, (status === 'searching' || status === 'matched') ? pollDelay : null);

  const startCapture = async () => {
    setBanner(null);
    pollDeadline.current = Date.now() + MAX_POLL_DURATION_MS;
    setPollDelay(POLL_INTERVAL_MS);
    postCompleteTicks.current = 0;
    matchedRef.current = false;
    finalizingRef.current = false;
    setStatus('searching');
    try {
      await postDocAssistantCapture(emailId);
    } catch (e) {
      logError(e);
      failCapture();
    }
  };

  return { status, banner, startCapture, dismissBanner };
};
